/**
 * A pro-forma as a page, `/proformas/:proformaNo` (docs/features/order-to-invoice/
 * 50_finance_sales_ledger_module_spec.md §9 item 3): a request for payment
 * before supply, never a VAT invoice and never sent to Xero. Its header, the
 * money asked for, paid and still owed, the lines it covers and the payments
 * received against it, with Record payment and Mark the excess refunded for
 * finance.
 *
 * No mock-up was drawn for this page: it is the smallest page consistent with
 * the order page's Payment step and Finance › Awaiting payment.
 */
import { useState } from "react";
import { useParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import {
  formatQuotedPrice,
  formatShortDateOrDash,
} from "@shared-core/domain/formatters";
import { orderStepFailure } from "@shared-core/domain/order-to-invoice-presentation";
import { paths } from "@shared-core/routing/routes";
import { RecordPaymentDialog } from "../app/components/domain/orders/OrderDialogs";
import {
  DocumentPageStatus,
  MoneySummary,
  PaymentsBlock,
  Section,
  StateBadge,
  headerFacts,
  useDocumentPage,
  usePdfDownload,
} from "../app/components/domain/sales-ledger/DocumentParts";
import { ObjectHeader } from "../app/components/patterns/ObjectHeader";
import { Button } from "../app/components/primitives/button";

export function ProformaPage() {
  const { proformaNo: rawNo } = useParams<{ proformaNo: string }>();
  const proformaNo = rawNo ? decodeURIComponent(rawNo) : "";
  const adapter = useDataAdapter();
  const { state, reload } = useDocumentPage(
    () => adapter.getProformaPage(proformaNo),
    [adapter, proformaNo],
  );
  const pdf = usePdfDownload();
  const [isRecording, setIsRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  if (state.kind !== "loaded")
    return (
      <DocumentPageStatus
        state={state}
        what="pro-forma"
        documentNo={proformaNo}
      />
    );
  const { proforma, header, payments } = state.page;

  const markExcessRefunded = async () => {
    setBusy(true);
    setNotice(null);
    try {
      await adapter.markExcessRefunded(proforma);
      setNotice("Marked: the excess is refunded.");
      await reload();
    } catch (err) {
      setNotice(orderStepFailure(err).summary);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <ObjectHeader
        crumbs={[
          { label: "Customers", to: paths.customers() },
          {
            label: header.customer_name ?? header.customer_code,
            to: paths.customerDetail({ customerId: header.customer_code }),
          },
          {
            label: "Invoices",
            to: paths.customerInvoices({ customerId: header.customer_code }),
          },
          { label: proforma.proforma_no },
        ]}
        title={`Pro-forma ${proforma.proforma_no}`}
        pills={
          <StateBadge state={proforma.state} label={proforma.state_label} />
        }
        value={formatQuotedPrice(proforma.gross_pence)}
        valueLabel="VAT included"
        facts={[
          ...headerFacts(header),
          {
            label: "Issued",
            value: formatShortDateOrDash(proforma.issued_on),
          },
          {
            label: "Valid until",
            value: formatShortDateOrDash(proforma.valid_until),
          },
          { label: "Pay by", value: `Quote ${proforma.payment_reference}` },
        ]}
        actions={
          <>
            <Button
              size="sm"
              variant="outline"
              disabled={pdf.busy}
              onClick={() => void pdf.viewPdf(proforma.pdf_url)}
            >
              View PDF
            </Button>
            {proforma.record_payment_url && (
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => setIsRecording(true)}
              >
                Record payment…
              </Button>
            )}
          </>
        }
      />

      {(pdf.notice ?? notice) && (
        <p role="status" className="text-muted-foreground text-sm">
          {pdf.notice ?? notice}
        </p>
      )}
      {proforma.superseded_by && (
        <p className="text-muted-foreground text-sm">
          Replaced by {proforma.superseded_by}.
        </p>
      )}

      <Section title="What it asks for">
        <p className="text-sm">
          Lines {proforma.line_nos.join(", ") || "none"}
        </p>
        <MoneySummary
          ariaLabel="Pro-forma money"
          rows={[
            { label: "Net", pence: proforma.net_pence, muted: true },
            { label: "VAT", pence: proforma.vat_pence, muted: true },
            { label: "Gross", pence: proforma.gross_pence, emphasis: true },
            { label: "Paid", pence: proforma.paid_pence },
            {
              label: "Still owed",
              pence: proforma.outstanding_pence,
              emphasis: proforma.outstanding_pence > 0,
            },
          ]}
        />
      </Section>

      {proforma.overpaid_sentence && (
        <div
          role="status"
          className="flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm dark:border-amber-700 dark:bg-amber-950"
        >
          <span>{proforma.overpaid_sentence}</span>
          {proforma.mark_excess_refunded_url && (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => void markExcessRefunded()}
            >
              Mark the excess refunded
            </Button>
          )}
        </div>
      )}
      {proforma.overpaid_refunded_on && (
        <p className="text-muted-foreground text-sm">
          The excess of {formatQuotedPrice(proforma.overpaid_pence)} was marked
          refunded in Xero on{" "}
          {formatShortDateOrDash(proforma.overpaid_refunded_on)}.
        </p>
      )}

      <PaymentsBlock payments={payments} />

      {isRecording && (
        <RecordPaymentDialog
          proforma={proforma}
          busy={busy}
          onClose={() => setIsRecording(false)}
          onRecord={async (input) => {
            setBusy(true);
            try {
              const recorded = await adapter.recordProformaPayment(
                proforma,
                input,
              );
              setNotice(recorded.summary);
              await reload();
              return null;
            } catch (err) {
              return orderStepFailure(err);
            } finally {
              setBusy(false);
            }
          }}
        />
      )}
    </div>
  );
}
